import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { formatCurrency, parseFormattedNumber } from '../../utils/formatters';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

const KassaTransactionModal = ({
  show,
  onHide,
  title,
  kassas = [],
  transports = [],
  onSubmit,
  loading = false,
  error = '',
  initialData = {},
  isExpense = false,
  editing = false,
  onTransportSearch = () => {}
}) => {
  const [formData, setFormData] = useState({
    kassa_id: '',
    transport_id: '',
    summa: '',
    foyda: '0',
    izoh: ''
  });
  
  const [selectedKassa, setSelectedKassa] = useState(null);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [transportOptions, setTransportOptions] = useState([]);
  const [isTransportLoading, setIsTransportLoading] = useState(false);
  

  useEffect(() => {
    if (show) {
      if (initialData && initialData.kassa_id) {
        setFormData(prev => ({
          ...prev,
          ...initialData,
          summa: initialData.summa ? formatCurrency(initialData.summa) : ''
        }));
        
        const kassa = kassas.find(k => k.id === initialData.kassa_id);
        if (kassa) setSelectedKassa(kassa);
        
        const transport = transports.find(t => t.id === initialData.transport_id);
        if (transport) {
          setSelectedTransport(transport);
        }
      } else {
        resetForm();
      }
    }
  }, [show, initialData, kassas, transports]);

  // Debounced search function
  const searchTransports = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.trim() === '') {
        setTransportOptions(transports.map(t => ({
          value: t.id,
          label: t.davlat_raqam,
          data: t
        })));
        return;
      }
      
      try {
        setIsTransportLoading(true);
        // Assuming you have a search endpoint or you can filter locally
        const filtered = transports.filter(transport => 
          transport.davlat_raqam?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        setTransportOptions(filtered.map(t => ({
          value: t.id,
          label: t.davlat_raqam,
          data: t
        })));
      } catch (error) {
        console.error('Error searching transports:', error);
      } finally {
        setIsTransportLoading(false);
      }
    }, 300),
    [transports]
  );

  // Initialize transport options when component mounts or transports change
  useEffect(() => {
    const options = transports.map(t => ({
      value: t.id,
      label: t.davlat_raqam,
      data: t
    }));
    setTransportOptions(options);
    
    // If editing and transport is selected, update the selected option
    if (initialData?.transport_id) {
      const transport = transports.find(t => t.id === initialData.transport_id);
      if (transport) {
        setSelectedTransport(transport);
      }
    }
  }, [transports, initialData]);

  const resetForm = () => {
    setFormData({
      kassa_id: '',
      transport_id: '',
      summa: '',
      foyda: '0',
      izoh: ''
    });
    setSelectedKassa(null);
    setSelectedTransport(null);
    setTransportOptions(transports.map(t => ({
      value: t.id,
      label: t.raqam,
      data: t
    })));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleKassaSelect = (e) => {
    const kassaId = parseInt(e.target.value);
    const kassa = kassas.find(k => k.id === kassaId);
    setSelectedKassa(kassa);
    setFormData(prev => ({
      ...prev,
      kassa_id: kassaId
    }));
  };

  const handleTransportChange = (selectedOption) => {
    if (selectedOption) {
      const transport = selectedOption.data;
      setSelectedTransport(transport);
      setFormData(prev => ({
        ...prev,
        transport_id: transport.id
      }));
      
      // Update the selected option in the options list to ensure it's in sync
      const optionExists = transportOptions.some(opt => opt.value === transport.id);
      if (!optionExists) {
        setTransportOptions(prev => [
          ...prev,
          {
            value: transport.id,
            label: transport.davlat_raqam,
            data: transport
          }
        ]);
      }
    } else {
      setSelectedTransport(null);
      setFormData(prev => ({
        ...prev,
        transport_id: ''
      }));
    }
  };
  
  const handleTransportInputChange = (inputValue) => {
    searchTransports(inputValue);
  };

  const handleSummaChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({
      ...prev,
      summa: value ? formatCurrency(parseInt(value)) : ''
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const amount = parseFormattedNumber(formData.summa);
    
    // Validate expense doesn't exceed available balance
    if (isExpense && selectedKassa && amount > selectedKassa.balance) {
      toast.error('Kassada yetarli mablag\' mavjud emas!');
      return;
    }
    
    const data = {
      ...formData,
      summa: amount
    };
    
    onSubmit(data);
  };

  const getKassaBalanceClass = () => {
    if (!selectedKassa) return '';
    return selectedKassa.balance >= 0 ? 'text-success' : 'text-danger';
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" style={{ zIndex: 2001 }}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="kassa_id">
                <Form.Label>Kassa</Form.Label>
                <Form.Select 
                  name="kassa_id" 
                  value={formData.kassa_id} 
                  onChange={handleKassaSelect}
                  required
                  disabled={loading || editing}
                >
                  <option value="">Kassani tanlang</option>
                  {kassas.map(kassa => (
                    <option key={kassa.id} value={kassa.id}>
                      {kassa.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedKassa && (
                  <div className="mt-2">
                    <small className={getKassaBalanceClass()}>
                      Balans: {formatCurrency(selectedKassa.balance)} UZS
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group controlId="transport_id">
                <Form.Label>Transport raqami</Form.Label>
                <Select
                  id="transport_id"
                  name="transport_id"
                  placeholder="Transport raqamini yozing..."
                  options={transportOptions}
                  value={transportOptions.find(option => option.value === formData.transport_id) || null}
                  onChange={handleTransportChange}
                  onInputChange={handleTransportInputChange}
                  isClearable
                  isSearchable
                  isLoading={isTransportLoading}
                  loadingMessage={() => "Yuklanmoqda..."}
                  noOptionsMessage={() => "Natija topilmadi"}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isDisabled={loading || editing}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                      height: '38px',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: '36px',
                      padding: '0 8px',
                    }),
                    input: (base) => ({
                      ...base,
                      margin: '0px',
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '36px',
                    }),
                  }}
                />
                {selectedTransport && (
                  <div className="mt-2">
                    <small className={selectedTransport.balance < 0 ? 'text-danger' : 'text-success'}>
                      Transport balansi: {formatCurrency(selectedTransport.balance || 0)} UZS
                      {selectedTransport.balance < 0 && ' (Qarzdor)'}
                    </small>
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="summa">
                <Form.Label>Summa (UZS)</Form.Label>
                <Form.Control
                  type="text"
                  name="summa"
                  value={formData.summa}
                  onChange={handleSummaChange}
                  placeholder="0"
                  required
                  disabled={loading}
                />
              </Form.Group>
              {isExpense && selectedKassa && formData.summa && (
                <div className="mt-2">
                  <small className={parseFormattedNumber(formData.summa) > selectedKassa.balance ? 'text-danger' : 'text-success'}>
                    {parseFormattedNumber(formData.summa) > selectedKassa.balance 
                      ? 'Kassada yetarli mablag\' mavjud emas!'
                      : `Qolgan balans: ${formatCurrency(selectedKassa.balance - parseFormattedNumber(formData.summa))} UZS`
                    }
                  </small>
                </div>
              )}
            </Col>
            <Col md={6}>
              <Form.Group controlId="izoh">
                <Form.Label>Izoh</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={1}
                  name="izoh"
                  value={formData.izoh}
                  onChange={handleChange}
                  placeholder="Izoh..."
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Bekor qilish
          </Button>
          <Button 
            variant={isExpense ? "danger" : "success"} 
            type="submit" 
            disabled={
              loading || 
              !formData.kassa_id ||
              !formData.summa ||
              parseFormattedNumber(formData.summa) <= 0 ||
              (isExpense && selectedKassa && parseFormattedNumber(formData.summa) > selectedKassa.balance)
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {isExpense ? (editing ? 'Chiqim taxrirlash...' : 'Chiqim qilish...') : (editing ? 'Kirim taxrirlash...' : 'Kirim qilish...')}
              </>
            ) : isExpense ? (editing ? 'Chiqim taxrirlash' : 'Chiqim qilish') : (editing ? 'Kirim taxrirlash' : 'Kirim qilish')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default KassaTransactionModal;
